import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "../../lib/api";
import { todayIso } from "../../lib/finance";
import {
  CONDITION_LABELS, MOVE_ACTIONS, RETIRE_REASON_LABELS, emptyMoveForm, moveNeedsReason, movePayload, moveProblem, newKey, procurementError,
} from "../../lib/procurement";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { HOLDER_KINDS, OWNER_KINDS, OWNER_LABELS, PartyField } from "./ProcurementBits";

const HOLDER_LABELS = { collaborator: "Une personne de l'équipe", external: "Une personne ou une organisation extérieure" };

const INTRO = {
  assign: "L'actif sort du stock et passe entre les mains de la personne choisie.",
  transfer: "L'actif change de mains. Le journal garde les deux détenteurs.",
  loan: "Un prêt est temporaire : à la restitution, l'actif revient à son détenteur actuel.",
  return: "L'actif revient en stock, ou chez son détenteur d'avant s'il était prêté.",
  location: "Où se trouve l'actif maintenant.",
  owner_change: "Le propriétaire juridique ne se déduit jamais de qui a payé : indiquez pourquoi il change. Le motif reste dans le journal de l'actif.",
  condition: "L'état constaté de l'actif.",
  retire: "Un actif retiré ne bouge plus et ne se modifie plus. Cette action est définitive.",
};

// One movement of an asset (assign, loan, return, transfer, location, owner, condition, retire): the same server rules, offered in words.
export function MoveDialog({ asset, kind, people, onClose, onDone }) {
  const [form, setForm] = useState(() => emptyMoveForm(kind, asset));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [key] = useState(newKey);
  const problem = moveProblem(kind, form, todayIso());
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/procurement/assets/${asset.id}/moves`, movePayload(kind, form, asset), { headers: { "Idempotency-Key": key } });
      toast.success(`${MOVE_ACTIONS[kind]} : c'est fait`);
      onDone(data);
    } catch (e) {
      setError(procurementError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  const danger = kind === "retire";
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid={`move-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>{MOVE_ACTIONS[kind]}</DialogTitle>
          <DialogDescription>{asset.label}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">{INTRO[kind]}</p>
          {["assign", "loan", "transfer"].includes(kind) && (
            <PartyField id="holder" withPharmacies label={kind === "loan" ? "Emprunteur" : "Nouveau détenteur"} value={form.holder} onChange={(holder) => set({ holder })}
              kinds={HOLDER_KINDS} kindLabels={HOLDER_LABELS} people={people} />
          )}
          {kind === "loan" && (
            <div className="space-y-1.5">
              <Label htmlFor="move-return">Retour prévu le</Label>
              <Input id="move-return" type="date" min={todayIso()} value={form.expected_return_on} onChange={(e) => set({ expected_return_on: e.target.value })} className="h-10" data-testid="move-return-date" />
            </div>
          )}
          {kind === "location" && (
            <div className="space-y-1.5">
              <Label htmlFor="move-location">Nouvel emplacement</Label>
              <Input id="move-location" value={form.location} maxLength={120} onChange={(e) => set({ location: e.target.value })} className="h-10" placeholder="Bureau de Dakar, domicile, pharmacie…" data-testid="move-location" />
            </div>
          )}
          {kind === "owner_change" && (
            <PartyField id="owner" label="Nouveau propriétaire" value={form.owner} onChange={(owner) => set({ owner })} kinds={OWNER_KINDS} kindLabels={OWNER_LABELS} people={people} />
          )}
          {kind === "condition" && (
            <div className="space-y-1.5">
              <Label>État</Label>
              <Select value={form.condition} onValueChange={(condition) => set({ condition })}>
                <SelectTrigger className="h-10" aria-label="État" data-testid="move-condition"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CONDITION_LABELS).map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {kind === "retire" && (
            <div className="space-y-1.5">
              <Label>Pourquoi est-il retiré ?</Label>
              <Select value={form.retire_reason} onValueChange={(retire_reason) => set({ retire_reason })}>
                <SelectTrigger className="h-10" aria-label="Cause du retrait" data-testid="move-retire-reason"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(RETIRE_REASON_LABELS).map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="move-reason">{moveNeedsReason(kind) ? "Motif" : "Note (facultative)"}</Label>
            <Textarea id="move-reason" rows={2} maxLength={300} value={form.reason} onChange={(e) => set({ reason: e.target.value })} data-testid="move-reason" />
          </div>
          {error && <p className="text-sm text-red-600" role="alert" data-testid="move-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant={danger ? "destructive" : "default"} onClick={submit} disabled={busy || !!problem} data-testid="move-submit" title={problem || undefined}>
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{MOVE_ACTIONS[kind]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

